import { useState, type FC, type ReactNode } from 'react';
import { Button, DismissibleTag, InlineNotification, Layer, NumberInput, Stack, StructuredListBody, StructuredListCell, StructuredListRow, StructuredListWrapper, TextInput, Tile } from '@carbon/react';

import { RECEBEDOR } from '@maestra/core/constants/checkout';
import type { LinhaDeAdicional } from '../../../logica/dinheiro/useCheckoutDaAssinatura';
import { ROTULOS, rotuloDoAviso } from '../../padroes/rotulos';
import styles from './Dinheiro.module.scss';

// AS PEÇAS DA COBRANÇA, as mesmas no checkout e no ajuste do plano: os adicionais (perfis e
// espaço), o cupom e o resumo do que se paga.

/**
 * Um adicional. O número do campo é o TOTAL que a pessoa recebe (e não o quanto se acrescenta):
 * mostrando o extra, quem queria os 30 GB do plano subia até 30 e comprava 30 GB a mais. O mínimo é
 * o que o plano já inclui, e o armazenamento anda de 10 em 10.
 *
 * ⚠️ O QUE SE DIGITA É UM RASCUNHO. Apagar o número para digitar outro passava pelo vazio, que virava
 * o mínimo na hora (e "5" virava 15). O valor só vai para a cobrança quando é válido, e ao sair do
 * campo ele encosta no limite e no passo.
 */
const CampoDoAdicional: FC<{ l: LinhaDeAdicional }> = ({ l }) => {
  const [rascunho, setRascunho] = useState<string | null>(null);
  const ajustar = (n: number) => {
    const noLimite = Math.min(l.max, Math.max(l.incluso, n));
    return l.incluso + Math.round((noLimite - l.incluso) / l.passo) * l.passo;
  };
  const valido = (n: number) => Number.isFinite(n) && n >= l.incluso && n <= l.max && (n - l.incluso) % l.passo === 0;
  return (
    <div>
      <NumberInput
        id={`adicional-${l.id}`}
        label={l.titulo}
        helperText={l.resumo}
        min={l.incluso}
        max={l.max}
        step={l.passo}
        value={rascunho ?? l.total}
        allowEmpty
        // O "Increment number" do Carbon, em português (vira o nome e a dica dos botões).
        translateWithId={(id: string) => (id === 'increment.number' ? `Mais ${l.unidade[1]}` : id === 'decrement.number' ? `Menos ${l.unidade[1]}` : id)}
        locale='pt-BR'
        invalid={rascunho !== null && rascunho !== '' && !valido(Number(rascunho))}
        invalidText={l.passo > 1 ? `De ${l.passo} em ${l.passo}, entre ${l.incluso} e ${l.max}.` : `Entre ${l.incluso} e ${l.max}.`}
        onChange={(e, { value }) => {
          // Os botões de mais e menos (um clique) já andam no passo: valem na hora. O Carbon manda
          // `direction` também ao digitar, então quem distingue é o tipo do evento.
          if (e.type === 'click') { setRascunho(null); l.mudar(ajustar(Number(value))); return; }
          const texto = String((e.target as HTMLInputElement)?.value ?? value ?? '');
          setRascunho(texto);
          const n = Number(texto);
          if (texto !== '' && valido(n)) l.mudar(n);
        }}
        onBlur={() => {
          if (rascunho === null) return;
          const n = Number(rascunho);
          l.mudar(rascunho === '' || !Number.isFinite(n) ? l.total : ajustar(n));
          setRascunho(null);
        }}
      />
      {l.nota && <p className={l.nota.alerta ? styles.nota : styles.apoio}>{l.nota.texto}</p>}
    </div>
  );
};

/** Os adicionais (perfis e espaço). */
export const Adicionais: FC<{ linhas: LinhaDeAdicional[] }> = ({ linhas }) => (
  <Stack gap={6}>
    {linhas.map((l) => <CampoDoAdicional key={l.id} l={l} />)}
  </Stack>
);

export interface DadosDoCupom {
  valor: string;
  mudar: (v: string) => void;
  aplicar: () => void;
  tirar: () => void;
  validando: boolean;
  erro: string | null;
  aplicado: string | null;
  /** O cupom que a assinatura já tem: tirado, ele pode não voltar (vencido ou esgotado). */
  aviso?: string | null;
  /** Tirou o cupom da assinatura e ainda não confirmou: dá para manter. */
  manter?: { rotulo: string; fazer: () => void } | null;
}

/**
 * O cupom: o código e "Aplicar"; aplicado, o código numa etiqueta que se tira, e o abatimento (o
 * valor e a porcentagem) inteiro ao lado. A validação é do servidor.
 */
export const CampoDoCupom: FC<{ c: DadosDoCupom }> = ({ c }) => {
  if (c.aplicado) {
    const [codigo, ...resto] = c.aplicado.split(' · ');
    return (
      <div className={styles.cupomAplicado}>
        <span className={styles.rotuloPequeno}>Cupom de desconto</span>
        <DismissibleTag type='green' text={codigo} title={`Tirar o cupom ${codigo}`} dismissTooltipLabel='Tirar o cupom' onClose={c.tirar} />
        {resto.length > 0 && <span className={styles.apoio}>{resto.join(' · ')}</span>}
        {c.aviso && <span className={styles.apoio}>{c.aviso}</span>}
      </div>
    );
  }
  return (
  <>
  {c.manter && (
    <div><Button kind='ghost' size='sm' onClick={c.manter.fazer}>{c.manter.rotulo}</Button></div>
  )}
  <div className={styles.linhaDoCupom}>
    <TextInput
      id='cupom'
      labelText='Cupom de desconto (opcional)'
      placeholder='Digite o código'
      maxLength={30}
      value={c.valor}
      invalid={!!c.erro}
      invalidText={c.erro ?? undefined}
      onChange={(e) => c.mudar(e.target.value.toUpperCase())}
      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (c.valor.trim()) c.aplicar(); } }}
    />
    <Button kind='tertiary' size='md' disabled={!c.valor.trim() || c.validando} onClick={c.aplicar}>
      {c.validando ? 'Validando…' : 'Aplicar'}
    </Button>
  </div>
  </>
  );
};

export interface LinhaDoResumo { rotulo: string; valor: string; forte?: boolean }

/** O resumo do que se paga: o item, as linhas (subtotal, economia, cupom) e o total. */
export const Resumo: FC<{
  titulo: string;
  item: { nome: string; detalhe: string; preco: string };
  linhas: LinhaDoResumo[];
  total?: LinhaDoResumo;
  depois?: { rotulo: string; valor: string; apoio: string };
  acima?: ReactNode;
  children?: ReactNode;
}> = ({ titulo, item, linhas, total, depois, acima, children }) => (
  <Tile className={styles.resumo}>
    <Layer>
      <Stack gap={6}>
        <h2 className={styles.tituloDoBloco}>{titulo}</h2>
        {acima}
        <div className={styles.item}>
          <div>
            <p className={styles.nomeDoItem}>{item.nome}</p>
            <p className={styles.apoio}>{item.detalhe}</p>
          </div>
          <p className={styles.precoDoItem}>{item.preco}</p>
        </div>
        <StructuredListWrapper isCondensed aria-label='Valores' className={styles.lista}>
          <StructuredListBody>
            {[...linhas, ...(total ? [{ ...total, forte: true }] : [])].map((l) => (
              <StructuredListRow key={l.rotulo}>
                <StructuredListCell className={l.forte ? styles.forte : undefined}>{l.rotulo}</StructuredListCell>
                <StructuredListCell className={`${styles.valor} ${l.forte ? styles.forte : ''}`}>{l.valor}</StructuredListCell>
              </StructuredListRow>
            ))}
          </StructuredListBody>
        </StructuredListWrapper>
        {depois && (
          <div>
            <div className={styles.item}>
              <p className={styles.texto}>{depois.rotulo}</p>
              <p className={`${styles.texto} ${styles.valor}`}>{depois.valor}</p>
            </div>
            <p className={styles.apoio}>{depois.apoio}</p>
          </div>
        )}
        {children}
      </Stack>
    </Layer>
  </Tile>
);

/** Quem aparece na fatura e no extrato: a empresa por trás da Maestra. */
export const Recebedor: FC = () => (
  <div>
    <p className={styles.apoio}>Compra segura · processada via Asaas</p>
    <p className={styles.apoio}>
      {'Na fatura do cartão e no extrato, a cobrança aparece como '}
      <strong>{RECEBEDOR.razaoSocial}</strong>
      {` · CNPJ ${RECEBEDOR.cnpj}`}
    </p>
  </div>
);

/** O erro da cobrança (`error`) ou um aviso antes de tentar (`warning`, com o próprio título). */
export const ErroDaCobranca: FC<{ texto: string | null; titulo?: string; tipo?: 'error' | 'warning' }> = ({ texto, titulo = 'Não deu certo', tipo = 'error' }) => (texto ? (
  <InlineNotification kind={tipo} lowContrast hideCloseButton title={titulo} subtitle={texto}
    statusIconDescription={rotuloDoAviso(tipo)} aria-label={ROTULOS.fecharAviso} className={styles.aviso} />
) : null);
